// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";

import Menu, { MappedLink } from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@synd/App/Components/Common/XngZone";
import FloatingTooltip, { FloatingTooltipStyles } from "@synd/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable from "@synd/App/Components/Header/HeaderClickable";
import { CustomAppState } from "@synd/App/Store/Reducers";
import { getMyAccountMapLinks, xIsAuthenticated } from "@synd/App/Store/Selectors/GeneralSelectors";
import { xGetFulfillmentLabel } from "@synd/App/Store/Selectors/HeaderShipToAddressSelector";
import MiniMyAccount from "@synd/App/Components/MiniMyAccount/MiniMyAccount";
import HeaderAccount from "@synd/XDL/Icons/HeaderAccount";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    // XNG-Change
    accountManagementLinks = "accountManagementLinks",
    orderManagementLinks = "orderManagementLinks",
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userName,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isPunchOutSession: getIsPunchOutSession(state),
    currentLocation: getLocation(state),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
    showInventory: state.context.showInventory,
    showPricing: state.context.showPricing,
    isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
    hasChangeLastQty:
        state.pages.vmiBins.selectVmiBinsChangedQty &&
        Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
    /**
     * XNG-Changes
     */
    isAuthenticated: xIsAuthenticated(state),
    isMyAccountPage: getCurrentPage(state).type === "MyAccountPage",
    accountManagementLinks: getMyAccountMapLinks(state, ownProps.fields.accountManagementLinks),
    orderManagementLinks: getMyAccountMapLinks(state, ownProps.fields.orderManagementLinks),
    isSignInOverlayOpen: state.components.signInOverlay.isOpen,
    isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    fulfillmentLabel: xGetFulfillmentLabel(state),
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    // XNG-Change
    signInTooltip?: FloatingTooltipStyles;
    myAccountTooltip?: FloatingTooltipStyles;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            padding: 6px 0;
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
            text-transform: uppercase;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
        // XNG-Change
        [fields.accountManagementLinks]: LinkModel[];
        [fields.orderManagementLinks]: LinkModel[];
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // XNG-Changes
    id,
    isAuthenticated,
    isMyAccountPage,
    accountManagementLinks,
    orderManagementLinks,
    isSignInOverlayOpen,
    isTabletScreen,
    fulfillmentLabel,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    // XNG-Changes
    const [isMenuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    // XNG-Changes: add isTabletScreen
    const showLabel = (visibilityState === "both" || visibilityState === "label") && !isTabletScreen;

    if (fields.icon === "User") {
        icon = HeaderAccount;
    }

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = (myAccountPageLink?.children?.filter(
        pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    ) ?? []) as MappedLink[];

    const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
            clickableProps: {
                css: css`
                    display: flex;
                    text-transform: uppercase;
                `,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();
            // XNG-Change
            // if (theme.signIn?.useOverlay) {
            //     setSignInOverlayIsOpen({ isOpen: true });
            //     return;
            // }

            // if (!signInUrl) {
            //     logger.warn("No url was found for SignInPage, defaulting to /SignIn");
            //     history.push("/MyAccount/SignIn");
            // } else {
            //     const returnUrl = currentLocation.pathname + currentLocation.search;
            //     if (returnUrl === "/") {
            //         history.push(`${signInUrl}`);
            //     } else {
            //         history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
            //     }
            // }
            if (signInUrl && isTabletScreen) {
                history.push(signInUrl);
                return;
            }
            setSignInOverlayIsOpen({ isOpen: true });
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    // XNG-Change
    const onMyAccountHandler = () => {
        if (isMyAccountPage) {
            return;
        }

        history.push(myAccountPageLink.url);
    };

    if (!isAuthenticated) {
        return (
            <StyledWrapper>
                <HeaderClickable
                    isOpen={isSignInOverlayOpen}
                    data-test-selector="signIn"
                    aria-label={translate("Sign In")}
                    extendedStyles={styles.headerClickable}
                    showIcon={showIcon}
                    icon={{
                        src: icon,
                    }}
                    onClick={onSignInHandler}
                >
                    {showLabel && translate("Account")}
                </HeaderClickable>

                <FloatingTooltip
                    onOpenChange={() => setSignInOverlayIsOpen({ isOpen: false })}
                    triggerComponent={<></>}
                    trigger="manual"
                    isOpen={isSignInOverlayOpen}
                    enabled={!isSignInPage}
                    overlay={true}
                    drawer={true}
                    extendedStyles={styles.signInTooltip}
                >
                    {createWidgetElement("SignIn/SignInExistingAccount", {
                        fields: {
                            fullWidthButton: true,
                        },
                    })}
                    <XngZone contentId={id} zoneName="content0" />
                </FloatingTooltip>
            </StyledWrapper>
        );
    }

    return (
        // <StyledWrapper {...styles.signOutWrapper}>
        //     {includeAccountMenu && userName && !currentUserIsGuest && (
        //         <Menu
        //             descriptionId="accountMenu"
        //             menuItems={menuItems}
        //             displayChangeCustomerLink={displayChangeCustomerLink}
        //             maxDepth={1}
        //             menuTrigger={
        //                 <Clickable {...styles.titleClickable} data-test-selector="header_signIn">
        //                     {showIcon && <Icon {...styles.titleIcon} src={icon} title={userName} />}
        //                     {showLabel && (
        //                         <Typography {...styles.titleTypography} data-test-selector="header_userName">
        //                             {userName}
        //                         </Typography>
        //                     )}
        //                 </Clickable>
        //             }
        //             cssOverrides={{
        //                 wrapper: css`
        //                     flex: 0 1 auto;
        //                     min-width: 0;
        //                 `,
        //             }}
        //             data-test-selector="header_accountMenu"
        //         />
        //     )}
        //     {(!includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
        //         <Clickable
        //             {...styles.titleClickable}
        //             onClick={onSignInHandler}
        //             href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}
        //             data-test-selector="header_signIn"
        //         >
        //             {showIcon && <Icon {...styles.titleIcon} src={icon} title={signInStatusText} />}
        //             {showLabel && <Typography {...styles.titleTypography}>{signInStatusText}</Typography>}
        //         </Clickable>
        //     )}
        //     {userName && !currentUserIsGuest && (
        //         <Clickable
        //             {...styles.signOutClickable}
        //             onClick={onSignOutHandler}
        //             data-test-selector="headerSignOutLink"
        //         >
        //             {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
        //         </Clickable>
        //     )}
        // </StyledWrapper>

        <FloatingTooltip
            offset={{
                mainAxis: 10,
            }}
            onOpenChange={open => setMenuOpen(open)}
            triggerComponent={
                <HeaderClickable
                    isOpen={isMenuOpen}
                    onClick={onMyAccountHandler}
                    data-test-selector="myAccount"
                    aria-label={translate("My Account")}
                    showIcon={showIcon}
                    icon={{
                        src: icon,
                    }}
                    extendedStyles={styles.headerClickable}
                    referenceId="mini-signin-reference"
                >
                    {showLabel && translate("Account")}
                </HeaderClickable>
            }
            enabled={!isTabletScreen}
            extendedStyles={styles.myAccountTooltip}
            placement="bottom-end"
            overlay={true}
            title={
                <StyledWrapper {...styles.titleWrapper}>
                    <IconMemo width={21} height={25} color="text.main" src={HeaderAccount} />
                    <Typography {...styles.title}>{fulfillmentLabel.addressValue}</Typography>
                </StyledWrapper>
            }
            referenceId="mini-signin-reference"
        >
            <MiniMyAccount
                displayChangeCustomerLink={displayChangeCustomerLink}
                accountManagementLinks={accountManagementLinks}
                orderManagementLinks={orderManagementLinks}
                onSignOutHandler={onSignOutHandler}
            />
        </FloatingTooltip>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
            // XNG-Change
            {
                displayName: "Account Management",
                name: fields.accountManagementLinks,
                editorTemplate: "ListField",
                fieldType: "Contextual",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const overriddenTitle = item.fields.overriddenTitle;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                },
                defaultValue: [],
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowCategories: item => false,
                        allowUrls: item => false,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
                isVisible: widget => !widget.fields.isSignInLink,
            },
            {
                displayName: "Product & Order Management",
                name: fields.orderManagementLinks,
                editorTemplate: "ListField",
                fieldType: "Contextual",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const overriddenTitle = item.fields.overriddenTitle;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                },
                defaultValue: [],
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowCategories: item => false,
                        allowUrls: item => false,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
                isVisible: widget => !widget.fields.isSignInLink,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
